/* eslint-disable no-restricted-syntax */
import React, { useState, useEffect } from 'react';
import useScript, { SCRIPT_LOADING_STATUS, ScriptLoadingStatus } from './useScript';
import { fetchMicrofrontendEntry } from './api';

// Type for storing cached entry paths
interface EntryPaths {
  [key: string]: string | false;
}

const entryPaths: EntryPaths = {};

/**
 * Load a Webpack micro-frontend's main entrypoint, and return its status.
 *
 * @param name identifier of microfrontend to load
 * @returns loading status of entrypoint script
 */
export const useMicrofrontend = (name: string): ScriptLoadingStatus => {
  const [url, setUrl] = useState<string | false | null>(null);

  useEffect(() => {
    const fetchUrl = async (): Promise<void> => {
      try {
        if (entryPaths[name] !== undefined) {
          setUrl(entryPaths[name]);
        } else {
          const result = await fetchMicrofrontendEntry(name);
          setUrl(result.data || false);
          entryPaths[name] = result.data || false;
        }
      } catch (error) {
        setUrl(false);
      }
    };
    fetchUrl();
  }, [name]);

  const status = useScript(url);
  return status;
};

/**
 * Load a Webpack micro-frontend's main entrypoint, and render wrapped component once loading succeeds or fails.
 *
 * @param name identifier of microfrontend to load
 * @param loadedProp name of boolean prop added to wrapped component, true if loading succeeded.
 */
export function withMicrofrontend<K extends string>(name: string, loadedProp: K) {
  return function <T extends React.ComponentType<any>>(
    WrappedComponent: T
  ): React.FC<Omit<React.ComponentProps<T>, K>> {
    // Create the wrapper component
    const WithMicrofrontendComponent = (props: Omit<React.ComponentProps<T>, K>) => {
      const status = useMicrofrontend(name);

      if (status === SCRIPT_LOADING_STATUS.READY || status === SCRIPT_LOADING_STATUS.ERROR) {
        // Create the additional prop
        const loadedPropValue = {
          [loadedProp]: status === SCRIPT_LOADING_STATUS.READY,
        };

        // Combine all props
        const allProps = { ...props, ...loadedPropValue } as React.ComponentProps<T>;

        // Explicitly return React element
        return React.createElement(WrappedComponent, allProps);
      }

      // Return null for React element
      return null;
    };

    // Set display name for better debugging
    WithMicrofrontendComponent.displayName = `WithMicrofrontend(${
      WrappedComponent.displayName || WrappedComponent.name || 'Component'
    })`;

    return WithMicrofrontendComponent as React.FC<Omit<React.ComponentProps<T>, K>>;
  };
}
